import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Check, X, Download, FileText, FileSpreadsheet } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

export default function QuotesPage() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ vehicule_id: "", investisseur_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [qRes, vRes] = await Promise.all([api.get("/quotes"), api.get("/vehicles")]);
      setQuotes(qRes.data); setVehicles(vRes.data);
      if (user?.role !== "investisseur") {
        const iRes = await api.get("/users?role=investisseur");
        setInvestors(iRes.data);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getVehicleName = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? `${v.marque} ${v.modele}` : "-"; };
  const getInvestorName = (id) => { const inv = investors.find(i => i.id === id); return inv ? `${inv.prenom} ${inv.nom}` : "-"; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quotes", { ...form, main_oeuvre: parseFloat(form.main_oeuvre || 0), frais_annexes: parseFloat(form.frais_annexes || 0) });
      toast.success("Devis cree");
      setShowAdd(false);
      setForm({ vehicule_id: "", investisseur_id: "", description: "", pieces: [], main_oeuvre: "", frais_annexes: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleValidate = async (quoteId, action) => {
    try {
      await api.put(`/quotes/${quoteId}/validate`, { action });
      toast.success(action === "valide" ? "Devis valide" : "Devis refuse");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const addPiece = () => setForm({ ...form, pieces: [...form.pieces, { nom: "", prix: 0 }] });
  const updatePiece = (i, field, value) => { const p = [...form.pieces]; p[i][field] = field === "prix" ? parseFloat(value || 0) : value; setForm({ ...form, pieces: p }); };

  const statusLabels = { en_attente: "En attente", valide: "Valide", refuse: "Refuse" };
  const statusClasses = { en_attente: "status-repos", valide: "status-actif", refuse: "status-immobilise" };

  const quotesExportCols = [
    { label: "Vehicule", accessor: (q) => getVehicleName(q.vehicule_id) },
    ...(user?.role !== "investisseur" ? [{ label: "Investisseur", accessor: (q) => getInvestorName(q.investisseur_id) }] : []),
    { label: "Description", key: "description" },
    { label: "Total", accessor: (q) => `${q.total} Frc` },
    { label: "Statut", accessor: (q) => statusLabels[q.statut] || q.statut },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="quotes-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Devis</h1>
          <p className="text-sm text-slate-500 mt-1">{quotes.length} devis</p>
        </div>
        {user?.role !== "investisseur" && (
          <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => setShowAdd(true)} data-testid="btn-add-quote">
            <Plus className="w-4 h-4 mr-2" />Nouveau devis
          </Button>
        )}
      </div>

      <div className="flex justify-end mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="btn-export-quotes">
              <Download className="w-4 h-4 mr-2" />Exporter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportCSV(quotes, quotesExportCols, "devis")} data-testid="export-quotes-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportExcel(quotes, quotesExportCols, "devis")} data-testid="export-quotes-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportPDF(quotes, quotesExportCols, "devis", "Liste des devis")} data-testid="export-quotes-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
              {user?.role !== "investisseur" && <TableHead className="font-semibold text-slate-600">Investisseur</TableHead>}
              <TableHead className="font-semibold text-slate-600">Description</TableHead>
              <TableHead className="font-semibold text-slate-600">Total</TableHead>
              <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.map(q => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="font-medium text-slate-900">{getVehicleName(q.vehicule_id)}</TableCell>
                {user?.role !== "investisseur" && <TableCell className="text-slate-600">{getInvestorName(q.investisseur_id)}</TableCell>}
                <TableCell className="text-slate-600 max-w-[200px] truncate">{q.description}</TableCell>
                <TableCell className="font-medium text-slate-900">{q.total} Frc</TableCell>
                <TableCell><Badge variant="outline" className={statusClasses[q.statut]}>{statusLabels[q.statut]}</Badge></TableCell>
                <TableCell className="text-right">
                  {q.statut === "en_attente" && user?.role === "investisseur" && (
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleValidate(q.id, "valide")} data-testid={`btn-validate-quote-${q.id}`}><Check className="w-4 h-4 mr-1" />Valider</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleValidate(q.id, "refuse")} data-testid={`btn-refuse-quote-${q.id}`}><X className="w-4 h-4 mr-1" />Refuser</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {quotes.length === 0 && <TableRow><TableCell colSpan={user?.role === "investisseur" ? 5 : 6} className="text-center text-slate-500 py-8">Aucun devis</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Add Quote Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-heading">Creer un devis</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicule</Label>
                <Select value={form.vehicule_id} onValueChange={v => setForm({...form, vehicule_id: v})}>
                  <SelectTrigger data-testid="select-quote-vehicle"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Investisseur</Label>
                <Select value={form.investisseur_id} onValueChange={v => setForm({...form, investisseur_id: v})}>
                  <SelectTrigger data-testid="select-quote-investor"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{investors.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.prenom} {inv.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} required data-testid="input-quote-desc" /></div>
            <div>
              <div className="flex items-center justify-between"><Label>Pieces</Label><Button type="button" variant="outline" size="sm" onClick={addPiece} data-testid="btn-add-quote-piece">+ Piece</Button></div>
              {form.pieces.map((p, i) => (
                <div key={i} className="flex gap-2 mt-2">
                  <Input placeholder="Nom" value={p.nom} onChange={e => updatePiece(i, "nom", e.target.value)} data-testid={`input-quote-piece-nom-${i}`} />
                  <Input type="number" placeholder="Prix" value={p.prix} onChange={e => updatePiece(i, "prix", e.target.value)} className="w-28" data-testid={`input-quote-piece-prix-${i}`} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Main d'oeuvre (Frc)</Label><Input type="number" value={form.main_oeuvre} onChange={e => setForm({...form, main_oeuvre: e.target.value})} data-testid="input-quote-mo" /></div>
              <div><Label>Frais annexes (Frc)</Label><Input type="number" value={form.frais_annexes} onChange={e => setForm({...form, frais_annexes: e.target.value})} data-testid="input-quote-frais" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-quote">Creer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
